import { Box, IconButton, TextField, Typography, useTheme, InputAdornment, Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useChatClient } from './useChatClient';
import { ChatConversationItem } from './ChatConversationItem';
import { useState, useEffect, useCallback, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LogoutIcon from '@mui/icons-material/Logout';
import { ChatNewConversation } from './ChatNewConversation';
import { useTranslation } from 'react-i18next';

export const ChatSidebar = ({ onConversationSelect, isStandalone = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { conversations, selectedConversation, setSelectedConversation, isLoadingConversations, setPendingFiles, typingUsersByConversation } = useChatClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Check if app is already installed
  useEffect(() => {
    // Check if running in standalone mode (PWA installed)
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://');

    setIsInstalled(isStandaloneMode);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback: open chat-app in new window/tab
      window.open('/chat-app', '_blank');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const filteredConversations = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return conversations
      .filter(conv => {
        if (!query) return true;
        return (conv.name || '').toLowerCase().includes(query);
      })
      .sort((a, b) => {
        // Sort pinned conversations to the top
        const aPinned = a.currentUserParticipant?.isPinned ? 1 : 0;
        const bPinned = b.currentUserParticipant?.isPinned ? 1 : 0;
        if (aPinned !== bPinned) return bPinned - aPinned;
        // Then sort by last message date
        const aDate = new Date(a.lastMessageAt || 0).getTime();
        const bDate = new Date(b.lastMessageAt || 0).getTime();
        return bDate - aDate;
      });
  }, [conversations, searchQuery]);

  const handleSelectConversation = useCallback((conversation) => {
    setSelectedConversation(conversation);
    onConversationSelect?.();
  }, [setSelectedConversation, onConversationSelect]);

  const handleFileDrop = useCallback((conversation, files) => {
    // Select the conversation and set pending files
    setSelectedConversation(conversation);
    setPendingFiles(files);
    onConversationSelect?.();
  }, [setSelectedConversation, setPendingFiles, onConversationSelect]);

  const showInstallButton = !isInstalled && !isStandalone;

  const handleLogout = () => {
    setMenuAnchor(null);
    if (window.standaloneLogout) {
      window.standaloneLogout();
    }
  };

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <Box className="flex flex-col h-full">
      {/* Header */}
      <Box className="p-4">
        <Box className="flex items-center justify-between mb-4">
          <Typography variant="h6" fontWeight="600">
            ExoChat
          </Typography>
          <Box className="flex items-center gap-1">
            <IconButton
              size="small"
              onClick={() => setNewChatOpen(true)}
              sx={{
                color: colors.info,
                '&:hover': { backgroundColor: `${colors.info}20` },
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </IconButton>
            {isStandalone && (
              <>
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{
                    color: colors.text,
                    '&:hover': { backgroundColor: colors.bgInput },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      backgroundColor: colors.glass,
                      backdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      minWidth: 150,
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" sx={{ color: colors.error }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ color: colors.error }}>
                      {t('Logout')}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder={t('Search...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.grey[500], fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.bgInput,
              borderRadius: '10px',
              '& fieldset': { border: 'none' },
            },
          }}
        />
      </Box>

      {/* Conversation List */}
      <Box className="flex-1 overflow-y-auto px-2">
        {isLoadingConversations ? (
          // Skeleton loading
          <Box className="flex flex-col gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Box
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl animate-pulse"
                sx={{ backgroundColor: colors.bgInput }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '12px',
                    backgroundColor: colors.card,
                  }}
                />
                <Box className="flex-1">
                  <Box
                    sx={{
                      width: '60%',
                      height: 14,
                      borderRadius: 1,
                      backgroundColor: colors.card,
                      mb: 1,
                    }}
                  />
                  <Box
                    sx={{
                      width: '80%',
                      height: 10,
                      borderRadius: 1,
                      backgroundColor: colors.card,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Box className="flex items-center justify-center h-32 p-4">
            <Typography sx={{ color: colors.grey[500], fontSize: '0.9rem' }}>
              {searchQuery ? t('No conversations found') : t('No conversations yet')}
            </Typography>
          </Box>
        ) : (
          filteredConversations.map((conversation) => (
            <ChatConversationItem
              key={conversation.id}
              conversation={conversation}
              isSelected={selectedConversation?.id === conversation.id}
              onSelect={handleSelectConversation}
              onFileDrop={handleFileDrop}
              typingUsers={typingUsersByConversation[conversation.id] || null}
            />
          ))
        )}
      </Box>

      {/* Footer with Install Button */}
      {showInstallButton && (
        <Box className="p-3">
          <IconButton
            size="small"
            onClick={handleInstallClick}
            title={t('Install ExoChat App')}
            sx={{
              color: colors.text,
              opacity: 0.5,
              '&:hover': { opacity: 1, backgroundColor: colors.bgInput },
            }}
          >
            <InstallMobileIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}

      {/* New Conversation Dialog */}
      <ChatNewConversation
        open={newChatOpen}
        onClose={() => setNewChatOpen(false)}
      />
    </Box>
  );
};
